import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
  ChevronRight, ChevronLeft, CheckCircle2, Brain, 
  Sparkles, Loader2, BookOpen, Target, Shield, AlertTriangle,
  TrendingUp, TrendingDown, Users, Share2, Download
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import PowerScoreShareCard from '../components/PowerScoreShareCard';

// Parent Assessment Questions - 3 Pillars with 2 questions each (6 total for now, can expand to 10)
const PARENT_QUESTIONS = [
  // Pillar 1: Academic Oversight (Confidence & Habits)
  {
    id: 1,
    pillar: 'Academic Oversight',
    pillarDescription: 'Confidence & Habits',
    icon: BookOpen,
    question: "When reviewing the 'Algebraic Foundations' in your student's Week 1 roadmap, how confident are you in your ability to audit their work for accuracy?",
    options: [
      { value: 1, label: "Not confident—I haven't seen this math in years." },
      { value: 2, label: "I can recognize if it looks right, but I can't solve it." },
      { value: 3, label: "Average confidence." },
      { value: 4, label: "High—I am comfortable with the numbers." },
      { value: 5, label: "Executive—I can teach the logic behind the math." }
    ]
  },
  {
    id: 2,
    pillar: 'Academic Oversight',
    pillarDescription: 'Confidence & Habits',
    icon: BookOpen,
    question: "How often do you proactively audit your athlete's 168-Hour Performance schedule to ensure their habits align with their goals?",
    options: [
      { value: 1, label: "Rarely—I hope they are doing it." },
      { value: 2, label: "Occasionally—usually when a grade or game is missed." },
      { value: 3, label: "Weekly—we check in on Sunday nights." },
      { value: 4, label: "Daily—performance tracking is a household standard." }
    ]
  },
  // Pillar 2: Tactical Management (Sports IQ)
  {
    id: 3,
    pillar: 'Tactical Management',
    pillarDescription: 'Sports IQ',
    icon: Target,
    question: "When your athlete makes a mistake in a game, how capable do you feel in facilitating a 'Film Room' style breakdown to identify the strategic correction?",
    options: [
      { value: 1, label: "Not capable—I just cheer from the stands." },
      { value: 2, label: "Somewhat—I can tell when they played poorly." },
      { value: 3, label: "Average—I can identify basic tactical errors." },
      { value: 4, label: "High—I understand play-calling and situational logic." }
    ]
  },
  {
    id: 4,
    pillar: 'Tactical Management',
    pillarDescription: 'Sports IQ',
    icon: Target,
    question: "How confident are you in helping your athlete navigate 'The Pressure' of a high-stakes recruiting or testing environment without adding to the stress?",
    options: [
      { value: 1, label: "Struggling—our household gets tense during big weeks." },
      { value: 2, label: "Average—we manage, but it's difficult." },
      { value: 3, label: "Excellent—we view pressure as a training tool for the pivot." }
    ]
  },
  // Pillar 3: Brand Governance (Personal Mission)
  {
    id: 5,
    pillar: 'Brand Governance',
    pillarDescription: 'Personal Mission',
    icon: Shield,
    question: "Does your household operate under a defined 'Personal Mission Statement' that serves as a filter for NIL opportunities and public behavior?",
    options: [
      { value: 1, label: "No—we haven't defined our brand identity yet." },
      { value: 2, label: "We have ideas, but nothing is written or enforced." },
      { value: 3, label: "We have a clear vision that we discuss regularly." }
    ]
  },
  {
    id: 6,
    pillar: 'Brand Governance',
    pillarDescription: 'Personal Mission',
    icon: Shield,
    question: "How confident are you in your ability to analyze the 'Tone and Syntax' of recruiter communications to protect your athlete's best interests?",
    options: [
      { value: 1, label: "Not confident—I'm not sure what the red flags are." },
      { value: 2, label: "Average—I can spot obvious issues." },
      { value: 3, label: "Executive—I am a high-level advocate and negotiator for my athlete." }
    ]
  }
];

// Calculate pillar scores
const calculatePillarScores = (answers) => {
  const pillarScores = {
    academic: { score: 0, maxScore: 0 },
    tactical: { score: 0, maxScore: 0 },
    brand: { score: 0, maxScore: 0 }
  };

  PARENT_QUESTIONS.forEach(q => {
    const answer = answers[q.id] || 0;
    const maxForQuestion = Math.max(...q.options.map(o => o.value));
    
    if (q.pillar === 'Academic Oversight') {
      pillarScores.academic.score += answer;
      pillarScores.academic.maxScore += maxForQuestion;
    } else if (q.pillar === 'Tactical Management') {
      pillarScores.tactical.score += answer;
      pillarScores.tactical.maxScore += maxForQuestion;
    } else if (q.pillar === 'Brand Governance') {
      pillarScores.brand.score += answer;
      pillarScores.brand.maxScore += maxForQuestion;
    }
  });

  return {
    academic: Math.round((pillarScores.academic.score / pillarScores.academic.maxScore) * 100),
    tactical: Math.round((pillarScores.tactical.score / pillarScores.tactical.maxScore) * 100),
    brand: Math.round((pillarScores.brand.score / pillarScores.brand.maxScore) * 100)
  };
};

const ParentDiagnostic = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // intro, questions, calculating, results
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [userInfo, setUserInfo] = useState({ name: '', email: '', athleteName: '' });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [studentScore, setStudentScore] = useState(null);

  // Try to fetch associated student score
  useEffect(() => {
    const fetchStudentScore = async () => {
      try {
        // Check if there's a student score in localStorage or fetch from API
        const storedStudentScore = localStorage.getItem('student_power_score');
        if (storedStudentScore) {
          setStudentScore(JSON.parse(storedStudentScore));
        }
      } catch (error) {
        console.log('No student score found');
      }
    };
    fetchStudentScore();
  }, []);

  const handleStartAssessment = (e) => {
    e.preventDefault();
    if (!userInfo.name || !userInfo.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    setStep('questions');
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    // Auto-advance to next question after a brief delay
    setTimeout(() => {
      if (currentQuestion < PARENT_QUESTIONS.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
  };

  const handleSubmit = async () => {
    // Check all questions answered
    if (Object.keys(answers).length < PARENT_QUESTIONS.length) {
      toast.error('Please answer all questions');
      return;
    }

    setStep('calculating');
    setLoading(true);

    try {
      // Calculate pillar scores
      const pillarScores = calculatePillarScores(answers);
      const totalScore = Math.round((pillarScores.academic + pillarScores.tactical + pillarScores.brand) / 3);
      
      // Mock student scores for comparison (in production, fetch from DB)
      const mockStudentScores = studentScore || {
        academic: 85,
        tactical: 90,
        brand: 75,
        total: 83
      };

      // Calculate gaps
      const gaps = {
        academic: pillarScores.academic - mockStudentScores.academic,
        tactical: pillarScores.tactical - mockStudentScores.tactical,
        brand: pillarScores.brand - mockStudentScores.brand
      };

      // Determine if there's an executive gap
      const hasExecutiveGap = gaps.academic < 0 || gaps.tactical < 0 || gaps.brand < 0;
      const worstGap = Math.min(gaps.academic, gaps.tactical, gaps.brand);
      const worstGapPillar = gaps.academic === worstGap ? 'Academic Oversight' : 
                            gaps.tactical === worstGap ? 'Tactical Management' : 'Brand Governance';

      // Submit to backend
      try {
        await api.post('/academy/parent-diagnostic/submit', {
          name: userInfo.name,
          email: userInfo.email,
          athlete_name: userInfo.athleteName,
          answers: Object.values(answers),
          total_score: totalScore,
          pillar_scores: pillarScores,
          gaps,
          has_executive_gap: hasExecutiveGap
        });
      } catch (err) {
        console.log('Backend submission failed, continuing with results');
      }

      // Store results
      const resultsData = {
        parentScore: totalScore,
        pillarScores,
        studentScores: mockStudentScores,
        gaps,
        hasExecutiveGap,
        worstGapPillar,
        userInfo
      };
      
      setResults(resultsData);
      
      // Brief pause for dramatic effect
      setTimeout(() => {
        setStep('results');
      }, 2000);

    } catch (error) {
      console.error('Error processing assessment:', error);
      toast.error('Failed to process assessment. Please try again.');
      setStep('questions');
    } finally {
      setLoading(false);
    }
  };

  const progress = ((currentQuestion + 1) / PARENT_QUESTIONS.length) * 100;
  const currentQ = PARENT_QUESTIONS[currentQuestion];
  const PillarIcon = currentQ?.icon || BookOpen;

  // Intro Screen
  if (step === 'intro') {
    return (
      <div className="min-h-screen bg-black text-white pt-20 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header with Logo */}
          <div className="text-center mb-10">
            {/* Scholar Parent Logo */}
            <img 
              src="/images/scholar-parent-logo-new.png" 
              alt="Scholar Parent Academy" 
              className="h-32 md:h-40 mx-auto mb-6"
            />
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              THE <span className="text-purple-500">AUDIT</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Discover Your Executive Score & Household Alignment
            </p>
          </div>

          {/* What You'll Learn */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-8">
            <h3 className="font-bold text-white mb-4">This audit will reveal:</h3>
            <div className="grid gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium text-white">Academic Oversight</p>
                  <p className="text-gray-500 text-sm">Your confidence in auditing and supporting academics</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-4 h-4 text-green-500" />
                </div>
                <div>
                  <p className="font-medium text-white">Tactical Management</p>
                  <p className="text-gray-500 text-sm">Your sports IQ and leadership capabilities</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-purple-500" />
                </div>
                <div>
                  <p className="font-medium text-white">Brand Governance</p>
                  <p className="text-gray-500 text-sm">Your personal mission and legacy protection</p>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Teaser */}
          <div className="bg-gradient-to-r from-purple-500/10 to-orange-500/10 border border-purple-500/30 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              <h4 className="font-bold text-white">Household Alignment Check</h4>
            </div>
            <p className="text-gray-400 text-sm">
              After completing The Audit, we'll compare your scores with your athlete's Power Score to identify any <span className="text-purple-400 font-bold">Executive Gaps</span> in your household leadership.
            </p>
          </div>

          {/* User Info Form */}
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <form onSubmit={handleStartAssessment} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-gray-300">Your Full Name *</Label>
                  <Input
                    id="name"
                    value={userInfo.name}
                    onChange={(e) => setUserInfo(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="athleteName" className="text-gray-300">Athlete's Name (Optional)</Label>
                  <Input
                    id="athleteName"
                    value={userInfo.athleteName}
                    onChange={(e) => setUserInfo(prev => ({ ...prev, athleteName: e.target.value }))}
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                    placeholder="Your athlete's name"
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-6 text-lg mt-4"
                >
                  BEGIN THE AUDIT
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-gray-600 text-sm mt-6">
            Your results are private and will be used to personalize your Executive Mirror experience.
          </p>
        </div>
      </div>
    );
  }

  // Questions Screen
  if (step === 'questions') {
    return (
      <div className="min-h-screen bg-black text-white pt-20 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-400">Question {currentQuestion + 1} of {PARENT_QUESTIONS.length}</span>
              <span className="text-sm text-purple-500 font-bold">{Math.round(progress)}% Complete</span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Pillar Badge */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <PillarIcon className="w-4 h-4 text-purple-500" />
            </div>
            <div>
              <span className="text-purple-500 font-bold text-sm tracking-wider uppercase">
                {currentQ.pillar}
              </span>
              <span className="text-gray-500 text-xs ml-2">({currentQ.pillarDescription})</span>
            </div>
          </div>

          {/* Question */}
          <h2 className="text-xl md:text-2xl font-bold mb-8 text-white leading-relaxed">
            {currentQ.question}
          </h2>

          {/* Options */}
          <div className="space-y-3 mb-8">
            {currentQ.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQ.id, option.value)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                  answers[currentQ.id] === option.value
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    answers[currentQ.id] === option.value
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-gray-600'
                  }`}>
                    {answers[currentQ.id] === option.value && (
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className={`text-sm ${
                    answers[currentQ.id] === option.value ? 'text-white' : 'text-gray-300'
                  }`}>
                    {option.label}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between gap-4">
            <Button
              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
              disabled={currentQuestion === 0}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            {currentQuestion === PARENT_QUESTIONS.length - 1 ? (
              <Button
                onClick={handleSubmit}
                disabled={Object.keys(answers).length < PARENT_QUESTIONS.length}
                className="bg-purple-500 hover:bg-purple-600 text-white font-bold"
              >
                Get My Results
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                disabled={!answers[currentQ.id]}
                className="bg-purple-500 hover:bg-purple-600 text-white font-bold"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Calculating Screen
  if (step === 'calculating') {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-8">
            <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
            <Brain className="absolute inset-0 m-auto w-10 h-10 text-purple-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Analyzing Your Executive Profile...</h2>
          <p className="text-gray-500">Calculating your Power Score and household alignment</p>
        </div>
      </div>
    );
  }

  // Results Screen
  if (step === 'results' && results) {
    const { parentScore, pillarScores, studentScores, gaps, hasExecutiveGap, worstGapPillar } = results;

    return (
      <div className="min-h-screen bg-black text-white pt-20 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header with Logo */}
          <div className="text-center mb-10">
            {/* Scholar Parent Logo */}
            <img 
              src="/images/scholar-parent-logo-new.png" 
              alt="Scholar Parent Academy" 
              className="h-28 md:h-36 mx-auto mb-6"
            />
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              YOUR <span className="text-purple-500">EXECUTIVE SCORE</span>
            </h1>
            <div className="text-7xl font-black text-purple-500 mb-2">{parentScore}</div>
            <p className="text-gray-400">Scholar Parent Executive Score</p>
          </div>

          {/* Side-by-Side Comparison Chart */}
          <Card className="bg-gray-900 border-gray-800 mb-8">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-white mb-6 text-center">Household Alignment</h3>
              
              {/* Comparison Headers */}
              <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                <div className="text-purple-500 font-bold">Parent</div>
                <div className="text-gray-500">Pillar</div>
                <div className="text-orange-500 font-bold">Athlete</div>
              </div>

              {/* Academic Oversight */}
              <div className="grid grid-cols-3 gap-4 items-center py-4 border-b border-gray-800">
                <div className="text-center">
                  <div className="text-2xl font-black text-white">{pillarScores.academic}%</div>
                </div>
                <div className="text-center">
                  <BookOpen className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                  <div className="text-sm text-gray-400">Academic</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-black text-white">{studentScores.academic}%</div>
                </div>
              </div>

              {/* Tactical Management */}
              <div className="grid grid-cols-3 gap-4 items-center py-4 border-b border-gray-800">
                <div className="text-center">
                  <div className="text-2xl font-black text-white">{pillarScores.tactical}%</div>
                </div>
                <div className="text-center">
                  <Target className="w-5 h-5 text-green-500 mx-auto mb-1" />
                  <div className="text-sm text-gray-400">Tactical</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-black text-white">{studentScores.tactical}%</div>
                </div>
              </div>

              {/* Brand Governance */}
              <div className="grid grid-cols-3 gap-4 items-center py-4">
                <div className="text-center">
                  <div className="text-2xl font-black text-white">{pillarScores.brand}%</div>
                </div>
                <div className="text-center">
                  <Shield className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                  <div className="text-sm text-gray-400">Brand</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-black text-white">{studentScores.brand}%</div>
                </div>
              </div>

              {/* Gap Indicators */}
              <div className="mt-6 grid grid-cols-3 gap-4 text-center text-sm">
                <div className={`flex items-center justify-center gap-1 ${gaps.academic >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {gaps.academic >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {gaps.academic >= 0 ? '+' : ''}{gaps.academic}%
                </div>
                <div></div>
                <div className={`flex items-center justify-center gap-1 ${gaps.tactical >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {gaps.tactical >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {gaps.tactical >= 0 ? '+' : ''}{gaps.tactical}%
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Executive Gap Alert */}
          {hasExecutiveGap && (
            <Card className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border-2 border-red-500/50 mb-8">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-red-500 mb-2">Executive Gap Detected</h3>
                    <p className="text-gray-300 mb-4">
                      Your athlete is out-pacing your tactical leadership in <span className="text-white font-bold">{worstGapPillar}</span>. 
                      Upgrade to the <span className="text-orange-500 font-bold">Strategist Tier</span> to bridge the gap and lead from the front.
                    </p>
                    <Button
                      onClick={() => navigate('/academy/enroll?type=parent')}
                      className="bg-purple-500 hover:bg-purple-600 text-white font-bold"
                    >
                      Upgrade to Strategist Tier
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Gap - Celebration */}
          {!hasExecutiveGap && (
            <Card className="bg-gradient-to-r from-green-500/10 to-purple-500/10 border-2 border-green-500/50 mb-8">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-black text-green-500 mb-2">Household Aligned!</h3>
                <p className="text-gray-300">
                  Congratulations! Your leadership is keeping pace with your athlete's development across all pillars.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Share Your Score Card */}
          <div className="mb-8">
            <PowerScoreShareCard
              userName={userInfo.name}
              totalScore={parentScore}
              academicScore={pillarScores.academic}
              tacticalScore={pillarScores.tactical}
              brandScore={pillarScores.brand}
              major={hasExecutiveGap ? "Growth Opportunity" : "Executive Leader"}
              cohort="Winter '26"
              isParent={true}
            />
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/academy?type=parent')}
              variant="outline"
              className="border-purple-500 text-purple-500 hover:bg-purple-500 hover:text-white font-bold"
            >
              Explore Executive Mirror
            </Button>
            <Button
              onClick={() => navigate('/academy/electives?view=parent')}
              className="bg-purple-500 hover:bg-purple-600 text-white font-bold"
            >
              Explore All Programs
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ParentDiagnostic;
